import { Mark, MarkType, Schema } from "prosemirror-model";
import { Transaction, Plugin, PluginKey, Command } from "prosemirror-state";

import { LinkEditBox } from "./component/link-editor";

import { BaseExtension } from "@/wc/text-editor/lib/core/base-extension";
import type { MenuFunction } from "@/wc/text-editor/lib/core/menu";

import {
    getPopupView,
    addPopup,
    closePopup,
    getPopupSpec,
} from "@/wc/text-editor/lib/core/popups";
import { expandWord } from "@/wc/text-editor/lib/helpers";

interface LinkEditPluginState {
    linkMark?: Mark;
    from: number;
    to: number;
    isActive: boolean;
    isEditable: boolean;
}

const LINK_EDIT_PLUGIN = new PluginKey<LinkEditPluginState>("LINK_EDIT_PLUGIN");

const setLinkHref =
    (linkType: MarkType, href: string): Command =>
    (state, dispatch) => {
        const pluginState = LINK_EDIT_PLUGIN.getState(state);
        if (!pluginState?.isActive) return false;

        if (dispatch) {
            const tr = state.tr.addMark(
                pluginState.from,
                pluginState.to,
                state.schema.mark(linkType, { href })
            );
            dispatch(tr.setMeta(LINK_EDIT_PLUGIN, { type: "DEACTIVATE" }));
        }
        return true;
    };

const removeLink: Command = (state, dispatch) => {
    const pluginState = LINK_EDIT_PLUGIN.getState(state);
    if (!pluginState?.isActive) return false;

    if (dispatch) {
        const tr = state.tr.removeMark(
            pluginState.from,
            pluginState.to,
            state.schema.marks.link
        );
        dispatch(tr.setMeta(LINK_EDIT_PLUGIN, { type: "DEACTIVATE" }));
    }
    return true;
};

const deactivateLink: Command = (st, dispatch) => {
    const pluginState = LINK_EDIT_PLUGIN.getState(st);
    if (!pluginState?.isActive) {
        return false;
    }
    if (dispatch) {
        const tr = st.tr.setMeta(LINK_EDIT_PLUGIN, { type: "DEACTIVATE" });
        dispatch(tr);
    }
    return true;
};

/**
 * Insert a link at current selection.
 */
const createLink: Command = (state, dispatch) => {
    if (!dispatch) return false;

    let range: {
        from: number;
        to: number;
    };

    if (state.selection.empty) {
        // Use whitespace boundary to expand the selection.
        range = expandWord(state.doc, state.selection.head, /\S/);
    } else {
        range = state.selection;
    }

    // Get the text content between the range
    const text = state.doc.textBetween(range.from, range.to);

    // Simple URL validation regex. ESlit rule is disabled because
    // it wrongfully flags the regex as invalid. Tried to remove the
    // "useless-escape", but it's actually needed.
    // eslint-disable-next-line no-useless-escape
    const urlRegex = /^(https?:\/\/)?[\w-]+(\.[\w-]+)+[\/#?]?.*$/i;
    const defaultHref = urlRegex.test(text) ? text : "";

    if (!defaultHref && state.selection.empty) {
        // Now just use word boundary in stead so we don't include dots etc.
        range = expandWord(state.doc, state.selection.head, /\w/);
    }

    const mark = state.schema.marks.link.create({ href: defaultHref });
    const tr = state.tr
        .addMark(range.from, range.to, mark)
        .setMeta(LINK_EDIT_PLUGIN, {
            type: "ACTIVATE",
            from: range.from,
            to: range.to,
            mark,
        });

    dispatch(tr);
    return true;
};

/**
 * Open link box popup.
 */
const openLinkBoxPopup = (
    transaction: Transaction,
    linkType: MarkType,
    pluginState: LinkEditPluginState
) => {
    return addPopup(transaction, "link-edit-box", {
        startPos: pluginState.from,
        endPos: pluginState.to,
        create: (view) => {
            return new LinkEditBox({
                href: {
                    value: pluginState.linkMark?.attrs.href || "",
                    setter: (href: string) => {
                        setLinkHref(linkType, href)(
                            view.state,
                            view.dispatch,
                            view
                        );
                        view.focus();
                    },
                },
                close: () => {
                    deactivateLink(view.state, view.dispatch, view);
                    view.focus();
                },
                removeLink: () => removeLink(view.state, view.dispatch, view),
                autofocus: false,
            });
        },
        onlyInEditMode: true,
        closeOnMouseLeave: false,
    });
};

/**
 * Open an edit box for setting the link target while the cursor is in a link.
 **/
const LinkEditPlugin = (linkType: MarkType) =>
    new Plugin<LinkEditPluginState>({
        key: LINK_EDIT_PLUGIN,
        state: {
            init: () => ({
                linkMark: undefined,
                from: 0,
                to: 0,
                isActive: false,
                isEditable: true,
            }),
            /**
             * Set the plugin state, which tracks if the popup is currently open and
             * if so, for which link.
             *
             * Link edit box is open in the new state if any of the following hold:
             *  - A new link is created through set_meta
             *  - The cursor is in the link that is active, with inclusive extent computation
             *  - The selection is non-empty, and includes a link mark in the node closest
             *      to the cursoe (selection head)
             *  - There is a link mark at the cursor
             *  - isCreatingLink is true, and the cursor is at pos
             */
            apply(tr, pluginState) {
                const meta = tr.getMeta(LINK_EDIT_PLUGIN);

                if (meta?.type === "ACTIVATE") {
                    return {
                        linkMark: meta.mark,
                        from: meta.from,
                        to: meta.to,
                        isActive: true,
                        isEditable: pluginState.isEditable,
                    };
                }

                if (meta?.type === "DEACTIVATE") {
                    return {
                        linkMark: undefined,
                        from: 0,
                        to: 0,
                        isActive: false,
                        isEditable: pluginState.isEditable,
                    };
                }

                // Maintain the current state but update positions if needed
                if (pluginState.isActive && tr.docChanged) {
                    const newFrom = tr.mapping.map(pluginState.from);
                    const newTo = tr.mapping.map(pluginState.to);
                    return {
                        ...pluginState,
                        from: newFrom,
                        to: newTo,
                    };
                }

                return pluginState;
            },
        },
        view() {
            return {
                update(view) {
                    const pluginState = LINK_EDIT_PLUGIN.getState(view.state);
                    if (pluginState) {
                        pluginState.isEditable = view.editable;
                    }
                },
            };
        },
        /**
         * When neccessary, append a transaction to set up stored link marks
         * and the link edit popup based on the plugin state.
         */
        appendTransaction(_tr, oldState, newState) {
            const oldPluginState = LINK_EDIT_PLUGIN.getState(oldState);
            const newPluginState = LINK_EDIT_PLUGIN.getState(newState);
            if (!newPluginState) return;

            if (newPluginState.linkMark && newPluginState.isEditable) {
                const transaction = newState.tr.addStoredMark(
                    newPluginState.linkMark
                );
                if (oldPluginState !== newPluginState) {
                    // Remove the old popup sync so we're guaranteed it is recreated.
                    const domNode = getPopupView(newState, "link-edit-box");
                    domNode?.remove();
                    // Update the popup based on the new / changed active link
                    return openLinkBoxPopup(
                        transaction,
                        linkType,
                        newPluginState
                    );
                }
                return transaction;
            } else if (getPopupSpec(newState, "link-edit-box")) {
                const transaction = oldPluginState?.linkMark
                    ? newState.tr.removeStoredMark(oldPluginState.linkMark)
                    : newState.tr;
                return closePopup(transaction, "link-edit-box");
            }
        },
        props: {
            handleClick: (view, pos) => {
                const state = view.state;
                const $pos = state.doc.resolve(pos);
                const marks = $pos.marks();
                const linkMark = marks.find(
                    (mark) => mark.type === state.schema.marks.link
                );

                if (linkMark) {
                    // Find the full extent of the link mark
                    let from = pos;
                    let to = pos;

                    // Search backwards
                    let start = $pos.start();
                    while (from > start) {
                        const found = state.doc.rangeHasMark(
                            from - 1,
                            from,
                            state.schema.marks.link
                        );
                        if (!found) break;
                        from--;
                    }

                    // Search forwards
                    let end = $pos.end();
                    while (to < end) {
                        const found = state.doc.rangeHasMark(
                            to,
                            to + 1,
                            state.schema.marks.link
                        );
                        if (!found) break;
                        to++;
                    }

                    view.dispatch(
                        state.tr.setMeta(LINK_EDIT_PLUGIN, {
                            type: "ACTIVATE",
                            from,
                            to,
                            mark: linkMark,
                        })
                    );
                    return true;
                }
                view.dispatch(
                    state.tr.setMeta(LINK_EDIT_PLUGIN, { type: "DEACTIVATE" })
                );
                return false;
            },
        },
    });

const toggleLink: Command = (state, dispatch, view) => {
    const pluginState = LINK_EDIT_PLUGIN.getState(state);

    if (pluginState?.isActive) {
        return removeLink(state, dispatch, view);
    }

    return createLink(state, dispatch, view);
};

export class LinkEditBoxExtension extends BaseExtension {
    plugins = (schema: Schema) => [LinkEditPlugin(schema.marks.link)];

    menu =
        (currentMenu: MenuFunction): MenuFunction =>
        (view) => [
            ...currentMenu(view),
            {
                name: "link",
                order: 29,
                group: "base",
                label: "Link",
                type: "toggle",
                icon: { name: "link-45deg", library: "component-assets" },
                active: (state) =>
                    Boolean(LINK_EDIT_PLUGIN.getState(state)?.linkMark),
                run: (state, dispatch, view) => {
                    toggleLink(state, dispatch, view);
                    view.focus();
                },
            },
        ];
}

export { LinkEditPlugin };
